import { BorderX, ColorX, LocaleX } from "IZOX/STATIC";
import { HStack, XButton, XDiv, XText } from "IZOX/_X";
import { FaPaperPlane } from "react-icons/fa";
import { FaFloppyDisk, FaPencil } from "react-icons/fa6";
import { MdAdd, MdAnnouncement, MdCancel, MdDelete, MdStream } from "react-icons/md";
import { v1 } from "uuid";

/**
 * @typedef {import("IZOX/__Typedef/common").lang} lang
 * @typedef {import("./_gears/QFDropUpload").qfdropuploadparams} qfdropuploadparams
 * @typedef {import("IZOX/_X/HStack").hstackparams} hstackparams
 * @typedef {import("IZOX/_X/VStack").vstackparams} vstackparams
 * @typedef {import("./_gears/QFFile").qffilepararms} qffilepararms
 * @typedef {import("IZOX/_X/XText").xtextparams} xtextparams
 * @typedef {import("./_gears/QFTabRadio").qftabradioparams} qftabradioparams
 * @typedef {import("./_gears/QFDate").qfdateparams} qfdateparams
 * @typedef {import("./_gears/QFDropdown").qfdropdownparams} qfdropdownparams
 * @typedef {import("./_gears/QFText").qftextparams} qftextparams
 * @typedef {import("IZOX/_X/XButton").xbuttonparams} xbuttonparams
 * @typedef {import("IZOX/_X/XDiv").xdivparams} xdivparams
 * @typedef {import("./_gears/QFSignature").qfsignatureparams} qfsignatureparams
 * 
 * @typedef {Object} optionItem
 * @property {String} val
 * @property {lang} cap
 * @property {String} theme
 * @property {String} color
 * 
 * @typedef {Object} xbuttonaddons
 * @property {boolean} withTooltip
 * @property {number} fontSize
 * @property {boolean} bold
 * @property {boolean} inverted
 * @property {boolean} iconOnRight
 * @property {number} iconSpacing
 * 
 * @typedef {xbuttonaddons & xbuttonparams} qfbuttonparams
 */

class QFButton {


  /**
   * 
   * @param {Function} submitFunc 
   * @param {boolean} [withIcon=true] 
   * @param {qfbuttonparams} [xbuttonparams={}] 
   * @returns 
   */
  static Cancel(submitFunc, withIcon = true, xbuttonparams = {}){
    return this.Simple(submitFunc, 
      {EN: "Cancel", TC: "取消"}, 
      withIcon ? <MdCancel/> : null,
      {bgColor: ColorX.CSS("red"), ...xbuttonparams}
    );
  }

  /**
   * 
   * @param {Function} submitFunc 
   * @param {boolean} [withIcon=true] 
   * @param {qfbuttonparams} [xbuttonparams={}] 
   * @returns 
   */
  static Delete(submitFunc, withIcon = true, xbuttonparams = {}){
    return this.Simple(submitFunc, 
      {EN: "Delete", TC: "刪除"}, 
      withIcon ? <MdDelete/> : null,
      {bgColor: ColorX.CSS("red"), ...xbuttonparams}
    );
  }

  /**
   * 
   * @param {Function} submitFunc 
   * @param {boolean} [withIcon=true] 
   * @param {qfbuttonparams} [xbuttonparams={}] 
   * @returns 
   */
  static Save(submitFunc, withIcon = true, xbuttonparams = {}){
    return this.Simple(submitFunc, 
      {EN: "Save Changes", TC: "儲存變更"}, 
      withIcon ? <FaFloppyDisk/> : null,
      {bgColor: ColorX.CSS("greyOut"), ...xbuttonparams}
    );
  }

  /**
   * 
   * @param {Function} submitFunc 
   * @param {boolean} [withIcon=true] 
   * @param {qfbuttonparams} [xbuttonparams={}] 
   * @returns 
   */
  static Draft(submitFunc, withIcon = true, xbuttonparams = {}){
    return this.Simple(submitFunc, 
      {EN: "Save as Draft", TC: "儲存為草稿"}, 
      withIcon ? <FaPencil/> : null,
      {bgColor: ColorX.CSS("purple"), ...xbuttonparams}
    );
  }

  /**
   * 
   * @param {Function} submitFunc 
   * @param {boolean} [withIcon=true] 
   * @param {qfbuttonparams} [xbuttonparams={}] 
   * @returns 
   */
  static Issue(submitFunc, withIcon = true, xbuttonparams = {}){
    return this.Simple(submitFunc, 
      {EN: "Issue", TC: "發布"}, 
      withIcon ? <MdAnnouncement/> : null,
      {bgColor: ColorX.CSS("gold"), ...xbuttonparams}
    );
  }

  /**
   * 
   * @param {Function} submitFunc 
   * @param {boolean} [withIcon=true] 
   * @param {qfbuttonparams} [xbuttonparams={}] 
   * @returns 
   */
  static Submit(submitFunc, withIcon = true, xbuttonparams = {}){
    return this.Simple(submitFunc, 
      {EN: "Submit", TC: "提交"}, 
      withIcon ? <FaPaperPlane/> : null,
      {bgColor: ColorX.CSS("green"), ...xbuttonparams}
    );
  }

  /**
   * 
   * @param {Function} clearFunc 
   * @param {boolean} [withIcon=true] 
   * @param {qfbuttonparams} [xbuttonparams={}] 
   * @returns 
   */
  static Clear(clearFunc, withIcon = true, xbuttonparams = {}){
    return this.Simple(clearFunc, 
      {EN: "Clear", TC: "清除"}, 
      withIcon ? <MdStream/> : null,
      {bgColor: ColorX.CSS("bgBlue"), ...xbuttonparams}
    );
  }

  /**
   * 
   * @param {Function} func 
   * @param {JSX.Element} icon
   * @param {lang} label  
   * @param {qfbuttonparams} param3 
   * @returns 
   */
  static Simple(func, label, icon, 
    {
      bgColor = ColorX.CSS("cmBlue"), 
      color = ColorX.CSS("pureWhite"), 
      hoverMode = "darken",
      withTooltip = true,
      fontSize = undefined,
      bold = false,
      iconOnRight = false,
      iconSpacing = 5,
      key = undefined,
      ...others
    } = {}){
    return (
      <XButton key={key} tooltip={withTooltip ? label : undefined} onPress={func} color={color} hoverMode={hoverMode}
        bgColor={bgColor} bW={1} round={5} minWidth={100}
        pV={2} pH={5} {...others}>
        <HStack spacing={iconSpacing} fullHeight>
          {!iconOnRight && <XDiv fitWidthToContent flexShrink={0}>{icon}</XDiv>}
          <XText fontSize={fontSize} bold={bold}>
            {LocaleX.Parse(label)}
          </XText>
          {iconOnRight && <XDiv fitWidthToContent flexShrink={0}>{icon}</XDiv>}
        </HStack>
      </XButton>
    );
  }

  /**
   * 
   * @param {*} func 
   * @param {Boolean} tooltip 
   * @param {*} icon 
   * @param {xbuttonparams} param3 
   * @returns 
   */
  static Icon(func, tooltip, icon, {
    size = 30,
    bgColor = ColorX.CSS("cmBlue"), 
    color = ColorX.CSS("pureWhite"), 
    hoverMode = "darken",
    ...others
  } = {}){
    return (
      <XButton tooltip={tooltip} onPress={func} 
        color={color} hoverMode={hoverMode}
        bgColor={bgColor} bW={1} round={5} 
        lockWidth={size} lockHeight={size}
        p={2} {...others}>
        <HStack fullHeight>
          {icon}
        </HStack>
      </XButton>
    );
  }

  /**
   * 
   * @param {Function} func 
   * @param {lang} label 
   * @param {JSX} icon 
   * @param {qfbuttonparams} param3 
   * @param {qftextparams} qftextparams
   * @returns 
   */
  static Bullet(func, label, icon, {
    bgColor = ColorX.CSS("cmBlue"), 
    color = ColorX.CSS("pureWhite"), 
    hoverMode = "darken",
    inverted = false,
    key = v1(),
    iconOnRight = false,
    ...others
  } = {}, qftextparams = {}){
    return (
      <XButton key={key} onPress={func} color={inverted ? bgColor : color} hoverMode={hoverMode}
        bgColor={inverted? color : bgColor} bW={1} round={25} minWidth={100}
        bColor={bgColor} 
        pV={2} pH={12} {...others}>
        <HStack spacing={10}>
          {!iconOnRight && icon}
          <XText {...qftextparams}>
            {LocaleX.Parse(label)}
          </XText>
          {iconOnRight && icon}
        </HStack>
      </XButton>
    );
  }

  /**
   * @typedef {Object} squareiconparams
   * @property {number} round
   * @property {number} p
   * @property {String} color
   * @property {boolean} inverted
   * @property {number} bW
   * @property {"darken" | "invert" | "none"} hoverMode
   * 
   * @param {Function} func 
   * @param {JSX.Element} icon 
   * @param {lang} tooltip 
   * @param {squareiconparams} param3 
   * @returns 
   */
  static SquareIcon(func, icon, tooltip = "", {
    round = 5,
    p = 5,
    color = ColorX.CSS("cmBlue"),
    inverted = false,
    bW = 0,
    hoverMode = "darken"
  } = {}){
    return (
      <XButton tooltip={tooltip} onPress={func} round={round} p={p}
        color={inverted ? ColorX.CSS("pureWhite") : color}
        bgColor={inverted ? color : ColorX.CSS("pureWhite")}
        bW={bW} bColor={color} hoverMode={hoverMode}>
        <HStack>
          {icon}
        </HStack>
      </XButton>
    );
  }

  /**
   * @typedef {Object} addboxparams
   * @property {number} height
   * @property {lang} tooltip 
   * @property {String} color
   * @property {number} iconSize
   * 
   * @param {Function} func 
   * @param {addboxparams} param1 
   * @returns 
   */
  static AddBox(func, {
    height = 60,
    tooltip,
    color = ColorX.CSS("cmBlue"),
    iconSize = 25
  } = {}){
    return (
      <XButton HAlign="[" opacity={0.5}
        onPress={func} flexShrink={0} tooltip={tooltip}
        bgColor={ColorX.CSS("pureWhite")} bStyle="dashed"
        fullWidth height={height}
        style={{backgroundImage: BorderX.Render({color: color, dashArray: [11, 8]})}}
        shadow pV={5} pL={10} mB={5} round={10}>
        <HStack>
          <MdAdd color={color} size={iconSize}/>
        </HStack>
      </XButton>
    );
  }

  /**
   * @typedef {Object} removeboxparams
   * @property {number} size
   * @property {lang} tooltip 
   * @property {String} color
   * @property {number} iconSize
   * 
   * @param {Function} func 
   * @param {removeboxparams} param1 
   * @returns 
   */
  static RemoveBox(func, {
    size = 30,
    tooltip,
    color = ColorX.CSS("red"),
    iconSize = 25
  } = {}){
    return (
      <XButton HAlign="]" opacity={0.5}
        onPress={func} flexShrink={0} tooltip={tooltip}
        bgColor={ColorX.CSS("pureWhite")} bStyle="dashed"
        width={size} height={size}
        style={{backgroundImage: BorderX.Render({color: color, dashArray: [11, 8]})}}
        shadow p={5} round={10} notInjectColor>
        <HStack fullHeight VAlign="-">
          <MdDelete color={color} size={iconSize}/>
        </HStack>
      </XButton>
    );
  }

}

export default QFButton;